// Admin v2 — Edzés kezelés (Figma ADM_15, 15b, 15c) és Edzői profil (ADM_15d, 15e).
// Edzésmódok: app_settings/training; statisztika: training_sessions; edzők: users (isCoach + coach* mezők,
// a régi CoachProfile sémája); edző meccsek: bookings (needsCoach / coachId).
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, limit, onSnapshot, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt, toDate } from '../data';
import {
  Avatar, Badge, Button, Card, ConfirmModal, Empty, Field, IconTile, Input, Modal, PageHeader, SectionTitle, Select, Spinner, StatCard, Tabs, TextArea, Toggle, Tone, useToast,
} from '../ui';

interface Mode { id: string; name: string; description: string; icon: string; tone: Tone; status: 'active' | 'soon' | 'recommended' }
interface Day { day: string; enabled: boolean; startTime: string; endTime: string }
interface Coach { uid: string; name: string; avatar: string | null; specialization: string; rate: string; experience: string; maxGroup: number; venues: string[]; bio: string; schedule: Day[] }

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_HU = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];
const DAY_SHORT = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
const DEFAULT_SCHEDULE: Day[] = DAY_KEYS.map((d) => ({ day: d, enabled: false, startTime: '17:00', endTime: '21:00' }));
const MODE_STATUS = { active: { t: 'Aktív', tone: 'green' as Tone }, soon: { t: 'Hamarosan', tone: 'gray' as Tone }, recommended: { t: 'Ajánlott', tone: 'cyan' as Tone } };
const MODES_REF = () => doc(db, 'app_settings', 'training');

const coachFrom = (id: string, v: Record<string, any>): Coach => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
  uid: id, name: v.displayName || v.username || 'Edző', avatar: v.avatarUrl || v.photoURL || null, specialization: v.coachSpecialization || '',
  rate: String(v.coachHourlyRate ?? ''), experience: String(v.coachExperience ?? ''), maxGroup: Number(v.coachMaxGroup ?? 4), venues: v.coachVenues ?? [],
  bio: v.coachBio || '', schedule: Array.isArray(v.coachWeekSchedule) && v.coachWeekSchedule.length === 7 ? v.coachWeekSchedule : DEFAULT_SCHEDULE,
});

/* ======================= Admin: Edzés kezelés ======================= */
export default function TrainingPage() {
  const toast = useToast();
  const [tab, setTab] = useState<'modes' | 'coaches' | 'matches'>('modes');
  const [modes, setModes] = useState<Mode[]>([]);
  const [stats, setStats] = useState<{ n: number; avg: number } | null>(null);
  const [coaches, setCoaches] = useState<Coach[] | null>(null);
  const [clubs, setClubs] = useState<Record<string, string>>({});
  const [edit, setEdit] = useState<Mode | 'new' | null>(null);
  const [del, setDel] = useState<Mode | null>(null);
  const [view, setView] = useState<Coach | null>(null);

  useEffect(() => onSnapshot(MODES_REF(), (s) => setModes((s.data()?.modes as Mode[]) ?? []), () => undefined), []);
  useEffect(() => {
    getDocs(query(collection(db, 'training_sessions'), where('date', '>=', Timestamp.fromDate(new Date(Date.now() - 30 * 864e5))), limit(10000)))
      .then((s) => { const pts = s.docs.map((d) => Number(d.data().points ?? 0)); setStats({ n: pts.length, avg: pts.length ? Math.round(pts.reduce((a, b) => a + b, 0) / pts.length) : 0 }); })
      .catch(() => setStats({ n: 0, avg: 0 }));
    getDocs(query(collection(db, 'users'), where('isCoach', '==', true))).then((s) => setCoaches(s.docs.map((d) => coachFrom(d.id, d.data())))).catch(() => setCoaches([]));
    getDocs(collection(db, 'clubs')).then((s) => setClubs(Object.fromEntries(s.docs.map((d) => [d.id, String(d.data().name ?? 'Klub')])))).catch(() => undefined);
  }, []);

  const saveModes = (l: Mode[]) => setDoc(MODES_REF(), { modes: l }, { merge: true });

  return (
    <div>
      <PageHeader title="Edzés kezelés" sub="Edzésmódok, ajánlott edzések és edzői programok" right={tab === 'modes' ? <Button icon="Plus" onClick={() => setEdit('new')}>Új edzésmód</Button> : undefined} />
      <Tabs value={tab} onChange={setTab} options={[{ value: 'modes', label: 'Edzésmódok' }, { value: 'coaches', label: 'Edzők' }, { value: 'matches', label: 'Edző meccsek' }]} />

      {tab === 'modes' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon="Target" tone="cyan" label="Edzések (30 nap)" value={stats ? fmtInt(stats.n) : '…'} />
            <StatCard icon="GraduationCap" tone="purple" label="Aktív edzők" value={coaches ? fmtInt(coaches.length) : '…'} valueColor="#a855f7" />
            <StatCard icon="TrendUp" tone="green" label="Átl. pont" value={stats ? fmtInt(stats.avg) : '…'} valueColor="#2bc48a" />
          </div>
          {modes.length === 0 ? <Card className="mt-6"><Empty>Még nincs edzésmód. Az „Új edzésmód” gombbal vehetsz fel.</Empty></Card> : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-6">
              {modes.map((m) => (
                <Card key={m.id} className="!p-4 flex items-start gap-3">
                  <IconTile icon={m.icon} tone={m.tone} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-white">{m.name}</div>
                    <div className="text-[12px] text-[#8a8f99] truncate">{m.description}</div>
                    <div className="mt-2"><Badge tone={MODE_STATUS[m.status].tone}>{MODE_STATUS[m.status].t}</Badge></div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setEdit(m)} aria-label="Szerkesztés" className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:text-white"><Icon name="PencilSimpleLine" size={15} /></button>
                    <button type="button" onClick={() => setDel(m)} aria-label="Törlés" className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444]"><Icon name="Trash" size={15} /></button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'coaches' && (coaches === null ? <Spinner /> : coaches.length === 0 ? <Card><Empty>Még nincs edző. A Felhasználók oldalon adhatsz edző jogosultságot.</Empty></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {coaches.map((c) => (
            <Card key={c.uid} className="!p-5">
              <div className="flex items-center gap-3"><Avatar src={c.avatar} name={c.name} size={44} /><div className="min-w-0"><div className="text-[14px] font-semibold text-white truncate">{c.name}</div><div className="text-[12px] text-[#00AFF2] truncate">{c.specialization || 'Edző'}</div></div></div>
              <div className="mt-3 text-[12px] text-[#8a8f99]">{c.rate ? `${fmtInt(Number(c.rate) || 0)} Ft / óra` : 'Óradíj nincs megadva'} · {c.venues.length} helyszín</div>
              <button type="button" onClick={() => setView(c)} className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#00AFF2] hover:underline">Részletek megtekintése <Icon name="ArrowRight" size={14} /></button>
            </Card>
          ))}
        </div>
      ))}

      {tab === 'matches' && <CoachMatches coaches={coaches ?? []} />}

      {edit && <ModeModal m={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSave={async (m) => {
        await saveModes(modes.some((x) => x.id === m.id) ? modes.map((x) => (x.id === m.id ? m : x)) : [...modes, m]); toast('Edzésmód mentve.');
      }} />}
      {del && <ConfirmModal title="Edzésmód törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)} text={<>Biztosan törlöd: <b className="text-white">{del.name}</b>?</>}
        onConfirm={async () => { await saveModes(modes.filter((x) => x.id !== del.id)); toast('Edzésmód törölve.'); setDel(null); }} />}
      {view && <CoachModal c={view} clubs={clubs} onClose={() => setView(null)} />}
    </div>
  );
}

function CoachMatches({ coaches }: { coaches: Coach[] }) {
  const [rows, setRows] = useState<{ id: string; title: string; venue: string; at: Date | null; players: number; coach: string }[] | null>(null);
  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, 'bookings'), where('needsCoach', '==', true), limit(100))).catch(() => null),
      getDocs(query(collection(db, 'bookings'), where('isCoachSession', '==', true), limit(100))).catch(() => null),
    ]).then(([a, b]) => {
      const seen = new Set<string>();
      const list = [...(a?.docs ?? []), ...(b?.docs ?? [])].filter((d) => !seen.has(d.id) && Boolean(seen.add(d.id))).map((d) => {
        const v = d.data();
        return {
          id: d.id, title: String(v.title ?? v.trainingTitle ?? 'Edzés'), venue: String(v.clubName ?? v.venueName ?? '—'),
          at: toDate(v.startAt ?? v.date), players: Array.isArray(v.participantIds) ? v.participantIds.length : Array.isArray(v.playerNames) ? v.playerNames.length : 1,
          coach: String(v.coachId ?? ''),
        };
      }).sort((x, y) => (x.at?.getTime() ?? 0) - (y.at?.getTime() ?? 0));
      setRows(list);
    });
  }, []);
  if (rows === null) return <Spinner />;
  if (!rows.length) return <Card><Empty>Nincs edzőt kereső vagy edzővel foglalt meccs.</Empty></Card>;
  const name = (id: string) => coaches.find((c) => c.uid === id)?.name;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {rows.map((r) => (
        <Card key={r.id} className="!p-5">
          <div className="flex items-center justify-between gap-2"><span className="text-[14px] font-semibold text-white truncate">{r.title}</span>{r.coach ? <Badge tone="green">Edző hozzárendelve</Badge> : <Badge tone="gold">Edző keresése</Badge>}</div>
          <div className="mt-3 text-[13px]">
            {[['MapPin', 'Helyszín', r.venue], ['CalendarBlank', 'Időpont', r.at ? r.at.toLocaleString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'], ['UsersThree', 'Játékosok', `${r.players} fő`], ['UserCheck', 'Edző', name(r.coach) ?? '–']].map(([ic, k, v]) => (
              <div key={k} className="flex justify-between py-1.5"><span className="inline-flex items-center gap-2 text-[#8a8f99]"><Icon name={ic} size={15} />{k}</span><span className="text-white">{v}</span></div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function CoachModal({ c, clubs, onClose }: { c: Coach; clubs: Record<string, string>; onClose: () => void }) {
  return (
    <Modal title="Edző adatai" onClose={onClose} width={520} footer={<Button kind="ghost" onClick={onClose}>Bezárás</Button>}>
      <div className="flex items-center gap-3"><Avatar src={c.avatar} name={c.name} size={54} /><div><div className="text-[18px] font-bold text-white">{c.name}</div><div className="text-[13px] text-[#00AFF2]">{c.specialization || 'Edző'}</div></div></div>
      {c.bio && <p className="mt-3 text-[13px] text-[#B8B8C3]">{c.bio}</p>}
      <h4 className="mt-5 mb-2 text-[13px] font-extrabold italic uppercase text-white">Információk</h4>
      {[['Coins', 'Óradíj', c.rate ? `${fmtInt(Number(c.rate) || 0)} Ft / óra` : '—'], ['Medal', 'Tapasztalat', c.experience ? `${c.experience} év` : '—'], ['UsersThree', 'Max. csoportlétszám', `${c.maxGroup} fő`]].map(([ic, k, v]) => (
        <div key={k} className="flex justify-between py-2 border-b border-[#31383D] text-[13px]"><span className="inline-flex items-center gap-2 text-[#8a8f99]"><Icon name={ic} size={15} />{k}</span><span className="text-white font-semibold">{v}</span></div>
      ))}
      <h4 className="mt-5 mb-2 text-[13px] font-extrabold italic uppercase text-white">Helyszínek</h4>
      <div className="flex flex-wrap gap-1.5">{c.venues.length ? c.venues.map((v) => <Badge key={v} tone="cyan">{clubs[v] ?? v}</Badge>) : <span className="text-[13px] text-[#8a8f99]">Nincs megadva</span>}</div>
      <h4 className="mt-5 mb-2 text-[13px] font-extrabold italic uppercase text-white">Órarend</h4>
      {c.schedule.filter((d) => d.enabled).length === 0 ? <span className="text-[13px] text-[#8a8f99]">Nincs megadva</span> : c.schedule.map((d, i) => d.enabled && (
        <div key={d.day} className="flex justify-between py-1.5 text-[13px]"><span className="text-[#B8B8C3]">{DAY_HU[i]}</span><span className="text-white font-semibold">{d.startTime} – {d.endTime}</span></div>
      ))}
    </Modal>
  );
}

function ModeModal({ m, onClose, onSave }: { m: Mode | null; onClose: () => void; onSave: (m: Mode) => Promise<void> }) {
  const [f, setF] = useState<Mode>(m ?? { id: `mode_${Date.now().toString(36)}`, name: '', description: '', icon: 'Target', tone: 'cyan', status: 'active' });
  return (
    <Modal title={m ? 'Edzésmód szerkesztése' : 'Új edzésmód'} onClose={onClose} width={480}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!f.name.trim()} onClick={async () => { await onSave({ ...f, name: f.name.trim() }); onClose(); }}>Mentés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Név" className="col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="pl. Around The Clock" /></Field>
        <Field label="Leírás" className="col-span-2"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="pl. Középhaladó · 15 perc" /></Field>
        <Field label="Ikon"><Select value={f.icon} onChange={(v) => setF({ ...f, icon: v })} options={['Target', 'ChartBar', 'Lightning', 'Clock', 'Trophy', 'Medal'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Szín"><Select value={f.tone} onChange={(v) => setF({ ...f, tone: v as Tone })} options={[['cyan', 'Cián'], ['green', 'Zöld'], ['gold', 'Arany'], ['purple', 'Lila'], ['red', 'Piros']].map(([v, l]) => ({ value: v, label: l }))} /></Field>
        <Field label="Állapot" className="col-span-2"><Select value={f.status} onChange={(v) => setF({ ...f, status: v as Mode['status'] })} options={Object.entries(MODE_STATUS).map(([k, v]) => ({ value: k, label: v.t }))} /></Field>
      </div>
    </Modal>
  );
}

/* ======================= Edző: saját edzői profil ======================= */
export function CoachProfilePage() {
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<'info' | 'schedule' | 'bookings'>('info');
  const [c, setC] = useState<Coach | null>(null);
  const [clubs, setClubs] = useState<{ id: string; name: string }[]>([]);
  const [rows, setRows] = useState<{ id: string; player: string; cp: number; at: Date | null; venue: string; dur: number; state: string }[] | null>(null);
  const [days, setDays] = useState<number[]>([]);
  const [gFrom, setGFrom] = useState('17:00');
  const [gTo, setGTo] = useState('21:00');

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (s) => setC(coachFrom(s.id, s.data() ?? {})), () => undefined);
    getDocs(collection(db, 'clubs')).then((s) => setClubs(s.docs.map((d) => ({ id: d.id, name: String(d.data().name ?? 'Klub') })))).catch(() => undefined);
    getDocs(query(collection(db, 'bookings'), where('coachId', '==', user.uid), limit(200))).then(async (s) => {
      const ids = Array.from(new Set(s.docs.map((d) => String(d.data().createdBy ?? '')).filter(Boolean)));
      const people: Record<string, { name: string; cp: number }> = {};
      for (let i = 0; i < ids.length; i += 10) {
        const us = await getDocs(query(collection(db, 'users'), where('__name__', 'in', ids.slice(i, i + 10)))).catch(() => null);
        us?.docs.forEach((d) => { people[d.id] = { name: d.data().username || d.data().displayName || 'Játékos', cp: Number(d.data().dqScore ?? 0) }; });
      }
      setRows(s.docs.map((d) => {
        const v = d.data();
        return { id: d.id, player: v.guestName || people[v.createdBy]?.name || '—', cp: people[v.createdBy]?.cp ?? 0, at: toDate(v.startAt ?? v.date), venue: String(v.clubName ?? v.venueName ?? '—'), dur: Number(v.durationMin ?? 60), state: String(v.state ?? v.status ?? 'confirmed') };
      }).sort((a, b) => (b.at?.getTime() ?? 0) - (a.at?.getTime() ?? 0)));
    }).catch(() => setRows([]));
    return unsub;
  }, [user]);

  const st = useMemo(() => ({ confirmed: { t: 'Megerősítve', tone: 'green' as Tone }, pending: { t: 'Függőben', tone: 'gold' as Tone }, cancelled: { t: 'Törölve', tone: 'red' as Tone } }), []);
  if (!c || !user) return <Spinner />;
  const upd = async (patch: Record<string, unknown>, ok: string) => { try { await updateDoc(doc(db, 'users', user.uid), { ...patch, updatedAt: Timestamp.now() }); toast(ok); } catch { toast('A mentés nem sikerült.', 'red'); } };

  return (
    <div>
      <PageHeader title="Edzői profil" sub="Kezeld edzői információidat és foglalásaidat" />
      <Tabs value={tab} onChange={setTab} options={[{ value: 'info', label: 'Információk' }, { value: 'schedule', label: 'Órarend' }, { value: 'bookings', label: `Foglalások (${rows?.length ?? 0})` }]} />

      {tab === 'info' && <InfoForm c={c} clubs={clubs} onSave={(p) => upd(p, 'Profil mentve.')} />}

      {tab === 'schedule' && (
        <div>
          <Card className="!p-4 flex items-center gap-3 flex-wrap">
            <span className="text-[13px] font-semibold text-white">Egységes időbeállítás</span>
            <div className="flex gap-1.5">{DAY_SHORT.map((d, i) => (
              <button key={d} type="button" onClick={() => setDays((x) => (x.includes(i) ? x.filter((y) => y !== i) : [...x, i]))}
                className={`h-[28px] min-w-[28px] px-2 rounded-full text-[11px] font-semibold ${days.includes(i) ? 'bg-[#00AFF2] text-white' : 'bg-[#31383D] text-[#B8B8C3]'}`}>{d}</button>
            ))}</div>
            <div className="flex-1" />
            <input type="time" value={gFrom} onChange={(e) => setGFrom(e.target.value)} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-white [color-scheme:dark]" />
            <span className="text-[#8a8f99]">–</span>
            <input type="time" value={gTo} onChange={(e) => setGTo(e.target.value)} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-white [color-scheme:dark]" />
            <button type="button" disabled={!days.length} onClick={() => setC({ ...c, schedule: c.schedule.map((d, i) => (days.includes(i) ? { ...d, enabled: true, startTime: gFrom, endTime: gTo } : d)) })}
              className="h-[37px] px-4 rounded-full border border-[#00AFF2] text-[#00AFF2] text-[13px] font-semibold disabled:opacity-40">Alkalmazás</button>
          </Card>
          <Card className="!p-5 mt-4 flex flex-col gap-2.5">
            {c.schedule.map((d, i) => (
              <div key={d.day} className="grid grid-cols-[100px_auto_1fr] items-center gap-4 min-h-[40px]">
                <span className="text-[13px] font-semibold text-white">{DAY_HU[i]}</span>
                <Toggle checked={d.enabled} onChange={(v) => setC({ ...c, schedule: c.schedule.map((x, j) => (j === i ? { ...x, enabled: v } : x)) })} />
                {d.enabled ? (
                  <div className="flex items-center gap-3">
                    <input type="time" value={d.startTime} onChange={(e) => setC({ ...c, schedule: c.schedule.map((x, j) => (j === i ? { ...x, startTime: e.target.value } : x)) })} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-white [color-scheme:dark]" />
                    <span className="text-[#8a8f99]">–</span>
                    <input type="time" value={d.endTime} onChange={(e) => setC({ ...c, schedule: c.schedule.map((x, j) => (j === i ? { ...x, endTime: e.target.value } : x)) })} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-white [color-scheme:dark]" />
                  </div>
                ) : <span className="text-[12px] text-[#8a8f99]">Nem elérhető</span>}
              </div>
            ))}
          </Card>
          <div className="flex justify-end mt-4"><Button icon="Check" onClick={() => upd({ coachWeekSchedule: c.schedule }, 'Órarend mentve.')}>Órarend mentése</Button></div>
        </div>
      )}

      {tab === 'bookings' && (rows === null ? <Spinner /> : (
        <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead><tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
              <th className="pl-5 pr-4 h-[39px] font-semibold">Játékos</th><th className="px-4 font-semibold">Időpont</th><th className="px-4 font-semibold">Helyszín</th><th className="px-4 font-semibold">Időtartam</th><th className="px-4 font-semibold">Állapot</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const s = st[r.state as keyof typeof st] ?? st.confirmed;
                return (
                  <tr key={r.id} className="border-t border-[#31383D] h-[56px] text-[13px]">
                    <td className="pl-5 pr-4"><div className="flex items-center gap-2.5"><Avatar name={r.player} size={30} /><div><div className="font-semibold text-white">{r.player}</div>{r.cp > 0 && <div className="text-[11px] text-[#8a8f99]">CP {r.cp}</div>}</div></div></td>
                    <td className="px-4 text-[#B8B8C3]">{r.at ? r.at.toLocaleString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td className="px-4 text-[#B8B8C3]">{r.venue}</td>
                    <td className="px-4 text-[#B8B8C3]">{r.dur} perc</td>
                    <td className="px-4"><Badge tone={s.tone}>{s.t}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <Empty>Még nincs foglalásod.</Empty>}
        </div>
      ))}
    </div>
  );
}

function InfoForm({ c, clubs, onSave }: { c: Coach; clubs: { id: string; name: string }[]; onSave: (p: Record<string, unknown>) => Promise<void> }) {
  const [f, setF] = useState({ specialization: c.specialization, rate: c.rate, experience: c.experience, maxGroup: String(c.maxGroup), bio: c.bio, venues: c.venues });
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-4 items-start">
      <Card className="!p-5">
        <SectionTitle>Alapadatok</SectionTitle>
        <div className="grid grid-cols-2 gap-x-3 gap-y-4">
          <Field label="Szakterület" className="col-span-2"><Input value={f.specialization} onChange={(e) => setF({ ...f, specialization: e.target.value })} placeholder="pl. Kiszálló specialista" /></Field>
          <Field label="Óradíj (Ft)"><Input inputMode="numeric" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value.replace(/[^\d]/g, '') })} /></Field>
          <Field label="Tapasztalat (év)"><Input inputMode="numeric" value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value.replace(/[^\d]/g, '') })} /></Field>
          <Field label="Max. csoportlétszám"><Input inputMode="numeric" value={f.maxGroup} onChange={(e) => setF({ ...f, maxGroup: e.target.value.replace(/[^\d]/g, '') })} /></Field>
          <Field label="Bemutatkozás" className="col-span-2"><TextArea rows={3} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} /></Field>
        </div>
      </Card>
      <Card className="!p-5">
        <SectionTitle>Helyszínek</SectionTitle>
        <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
          {clubs.map((cl) => {
            const on = f.venues.includes(cl.id);
            return (
              <button key={cl.id} type="button" onClick={() => setF({ ...f, venues: on ? f.venues.filter((x) => x !== cl.id) : [...f.venues, cl.id] })}
                className={`flex items-center gap-3 h-[42px] px-3 rounded-xl border text-left text-[13px] ${on ? 'bg-[#00AFF2]/[0.12] border-[#00AFF2]/40 text-white' : 'bg-[#1d232a] border-[#31383D] text-[#B8B8C3]'}`}>
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md border ${on ? 'bg-[#00AFF2] border-[#00AFF2] text-white' : 'border-[#31383D]'}`}>{on && <Icon name="Check" size={13} />}</span>{cl.name}
              </button>
            );
          })}
          {clubs.length === 0 && <Empty>Nincs klub.</Empty>}
        </div>
      </Card>
      <div className="xl:col-span-2 flex justify-end">
        <Button icon="Check" onClick={() => onSave({ coachSpecialization: f.specialization.trim(), coachHourlyRate: f.rate, coachExperience: f.experience, coachMaxGroup: Number(f.maxGroup) || 4, coachBio: f.bio.trim(), coachVenues: f.venues })}>Profil mentése</Button>
      </div>
    </div>
  );
}
